import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogPanel,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Link,
    Switch,
    TextField,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { IconCancel, IconClose, IconInfoCircle, IconResetSmall, ImageFolderSmallOn } from "@mzc-cloudplex/icons";
import TagsInput from "@features/tags-input/tags-input";
import SelectFolderPathModal from "@components/modals/SelectFolderPath";
import { clearModalList, getCollectionsModal } from "@modules/collections";
import { clearAssetList, createCollection, getAssetsList } from "@modules/collection";
import { setNotification } from "@modules/notification";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";

import TooltipButton from "../../components_v2/tooltip-button/tooltip-button";
import { StyledGrid } from "@components_v2/grid/grid.styled";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";
import FolderLocation from "@components_v2/folder-location/folder-location";
import RecentFolderLocation from "@components_v2/recent-folder-location/recent-folder-location";
import { StyledErrorAlert } from "@components_v2/alert/alert.styled";
import { LinkedAssetsColumn } from "@features/autocomplete-table/autocomplete-table-columns/autocomplete-table-columns-assets";
import Tooltip from "@components_v2/Tooltip/tooltip";
import AutocompleteTableList from "@features/autocomplete-table/autocomplete-table-list";
import StickyHeaderDialog from "@components_v2/dialog/sticky-header-dialog";

export const DialogCreateCollection = ({ open, assetItems, onClose, afterMutation, assetFolder, initialFolder }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const MAX_ASSETS = 1000;

    const { setLocalStorageRecentLocation } = useVirtualFolderPath({
        domain: "COLLECTION",
    });
    const LIMIT = 20;
    const [tags, setTags] = useState([]);
    const [description, setDescription] = useState({ validationErrors: null, value: null });
    const [name, setName] = useState({ validationErrors: null, value: null });
    const [editValue, setEditValue] = useState(
        assetFolder
            ? {
                  items: {
                      virtualFolder: { id: assetFolder.id, recursive: true },
                  },
              }
            : {},
    );
    const [assetSearchValue, setAssetSearchValue] = useState({ searchedValue: null, debouncedValue: null });
    const [collectionSearchValue, setCollectionSearchValue] = useState({ searchedValue: null, debouncedValue: null });
    const [folderLocation, setFolderLocation] = useState([]);
    const [modal, setModal] = useState("");
    const [selectedAssets, setSelectedAssets] = useState(assetItems ?? []);

    const project = useSelector(({ project }) => project);
    const collections = useSelector(({ collections }) => collections.create);
    const collection = useSelector(({ collection }) => collection);

    const onHandleCreateCollection = async () => {
        try {
            const response = await dispatch(createCollection(editValue));
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(
                        `common::msg::Collection '{{name}}' has been created.`,
                        `Collection '{{name}}' has been created.`,
                        {
                            name: response?.data?.name,
                        },
                    ),
                }),
            );
            updateRecentLocation();
            afterMutation && afterMutation(response.data.id);
            onClose();
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents: t(
                        `common::msg::Error adding collection '{{name}}'. {{line}} Please refresh the page and try again.`,
                        `Error adding collection '{{name}}'. {{line}} Please refresh the page and try again.`,
                        {
                            name,
                            line: "\n",
                        },
                    ),
                }),
            );
        }
    };

    const updateRecentLocation = useCallback(() => {
        const folderId = editValue.virtualFolder?.id;
        setLocalStorageRecentLocation({
            folderId,
        });
    }, [editValue, folderLocation]);

    const onHandleSubmitFolderPath = ({ folderId, folderPath }) => {
        const fLocation = folderPath?.map((item) => {
            return { name: item.name };
        });
        setEditValue({ ...editValue, virtualFolder: { id: folderId } });
        setFolderLocation(fLocation);
        onToggleModal();
    };

    const onToggleModal = (key, value) => {
        if (value) {
            setModal(key);
        } else {
            setModal("");
        }
    };

    const onChangeRecentLocation = ({ locationId, locations }) => {
        setEditValue({ ...editValue, virtualFolder: { id: locationId } });
        setFolderLocation(locations);
    };

    const isAssetsCountError = useMemo(() => {
        return selectedAssets.length > MAX_ASSETS;
    }, [selectedAssets]);

    const onHandleDeleteAsset = (id) => {
        const newAssets = selectedAssets.filter((asset) => asset.id !== id);
        setSelectedAssets(newAssets);
    };

    useEffect(() => {
        const searchedValue = collectionSearchValue.searchedValue;
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            return setCollectionSearchValue({ ...collectionSearchValue, debouncedValue: searchedValue });
        }, 300);
        return () => clearTimeout(debounce);
    }, [collectionSearchValue.searchedValue]);

    useEffect(() => {
        const searchedValue = assetSearchValue.searchedValue;
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            return setAssetSearchValue({ ...assetSearchValue, debouncedValue: searchedValue });
        }, 300);
        return () => clearTimeout(debounce);
    }, [assetSearchValue.searchedValue]);

    useEffect(() => {
        dispatch(clearAssetList());
        const params = { name: assetSearchValue.debouncedValue, limit: LIMIT, offset: 0 };
        dispatch(getAssetsList(project.id, params));
    }, [assetSearchValue.debouncedValue]);

    useEffect(() => {
        dispatch(clearModalList());
        const params = { name: collectionSearchValue.debouncedValue };
        dispatch(getCollectionsModal(params));
    }, [collectionSearchValue.debouncedValue]);

    useEffect(() => {
        if (!description.validationErrors) setEditValue({ ...editValue, description: description.value });
    }, [description]);

    useEffect(() => {
        if (!name.validationErrors) setEditValue({ ...editValue, name: name.value });
    }, [name]);

    useEffect(() => {
        if (initialFolder) {
            onHandleSubmitFolderPath(initialFolder);
        }
    }, [initialFolder]);

    useEffect(() => {
        if (selectedAssets.length === 0) return;

        setEditValue({
            ...editValue,
            primaryAsset: { id: selectedAssets[0].id },
            items: {
                assetIds: selectedAssets.map((asset) => asset.id),
            },
        });
    }, [selectedAssets]);

    return (
        <>
            {modal === "selectFolderPath" && (
                <SelectFolderPathModal
                    open
                    domain={"Collection"}
                    submitText={`Select`}
                    width={"600px"}
                    title={"Select location"}
                    subFoldersOption={false}
                    useNewFolder={false}
                    onSubmit={onHandleSubmitFolderPath}
                    onClose={() => onToggleModal()}
                />
            )}
            <StickyHeaderDialog
                onClose={onClose}
                open={open}
                variant="expandable"
                sx={{ ".MuiPaper-root": { maxWidth: "1240px", minWidth: "1095px" } }}
            >
                <DialogTitle>
                    <Grid container>
                        <Typography variant="h3">{t(`common::label::Create`, `Create`)}</Typography>
                    </Grid>
                    <TooltipButton
                        circled
                        title={t(`common::label::Close`, "Close")}
                        onClickButton={onClose}
                        buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                    />
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <Grid container gap={3}>
                            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                <Typography
                                    variant={`body1_500`}
                                    component={"p"}
                                    color={theme.palette.secondary[800]}
                                    required={true}
                                >
                                    {t(`common::label::Name`)}
                                </Typography>
                                <Grid item sx={{ width: `-webkit-fill-available` }}>
                                    <TextField
                                        fullWidth
                                        helperText={name.validationErrors?.message ?? ""}
                                        error={name.validationErrors}
                                        placeholder={t("common::msg::Please enter the name.", "Please enter the name.")}
                                        variant="outlined"
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            setName({
                                                value: e.target.value,
                                                validationErrors: validateInput(e.target.value, {
                                                    required: true,
                                                    maxLength: LIMIT_CHAR.name,
                                                }),
                                            });
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                <Typography variant={`body1_500`} component={"p"} color={theme.palette.secondary[800]}>
                                    {t(`common::label::Description`)}
                                </Typography>
                                <Box width={"100%"}>
                                    <FormControl fullWidth variant="outlined">
                                        <TextField
                                            sx={{
                                                minWidth: "584px",
                                            }}
                                            helperText={description.validationErrors?.message}
                                            multiline
                                            placeholder={t(
                                                "common::msg::Please enter the description.",
                                                "Please enter the description.",
                                            )}
                                            variant="outlined"
                                            error={description.validationErrors}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                setDescription({
                                                    value: e.target.value,
                                                    validationErrors: validateInput(e.target.value, {
                                                        maxLength: LIMIT_CHAR.description,
                                                    }),
                                                });
                                            }}
                                        />
                                        <FormHelperText>
                                            <Grid alignItems="center" component="span" container gap={1}>
                                                {(description.value || "").length} / {LIMIT_CHAR.description}
                                            </Grid>
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                            </Grid>

                            {/*Location 영역*/}
                            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                <Typography
                                    variant={`body1`}
                                    component={"p"}
                                    color={theme.palette.secondary[800]}
                                    required={true}
                                >
                                    {t(`common::label::Location`)}
                                </Typography>
                                <StyledGrid item theme={theme}>
                                    <Grid container flexDirection={"row"} gap={2} alignItems={"center"}>
                                        <Grid item sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                                            <FolderLocation
                                                folderLocation={folderLocation}
                                                onClickChange={() => setModal("selectFolderPath")}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <RecentFolderLocation
                                                domain={"COLLECTION"}
                                                onChangeRecentLocation={onChangeRecentLocation}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                size={"small"}
                                                variant={"text"}
                                                startIcon={<IconResetSmall size={16} />}
                                                color={"greySecondary"}
                                                onClick={() =>
                                                    initialFolder
                                                        ? onHandleSubmitFolderPath(initialFolder)
                                                        : setFolderLocation([])
                                                }
                                            >
                                                <Typography variant={"body3"}>{t(`common::label::Reset`)}</Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </StyledGrid>
                            </Grid>

                            {/*Metadata 영역*/}
                            <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                <Typography variant={`body1_500`} component={"p"} color={theme.palette.secondary[800]}>
                                    {t(`common::label::Metadata`)}
                                </Typography>
                                <Grid
                                    item
                                    sx={{
                                        width: `-webkit-fill-available`,
                                        padding: "20px",
                                        borderRadius: `4px`,
                                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                                    }}
                                >
                                    <Grid container alignItems={"center"}>
                                        <Grid
                                            item
                                            sx={{ alignSelf: "flex-start", minWidth: "244px" }}
                                            alignItems={"center"}
                                        >
                                            <Typography variant={"body1_500"} component={"p"}>
                                                {t(`common::label::Tags`, `Tags`)}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            sx={{
                                                flex: 1,
                                                alignSelf: "flex-start",
                                            }}
                                        >
                                            <TagsInput
                                                tags={tags}
                                                isEdit={false}
                                                disabled={collection.pending}
                                                onDeleteTag={(index) => {
                                                    setTags(tags.filter((t, idx) => idx !== index));
                                                    setEditValue({
                                                        ...editValue,
                                                        tags: tags
                                                            .filter((t, idx) => idx !== index)
                                                            .map((item) => item.value),
                                                    });
                                                }}
                                                onAddTag={(tag) => {
                                                    setTags([...tags, tag]);
                                                    setEditValue({
                                                        ...editValue,
                                                        tags: [...tags, tag].map((item) => item.value),
                                                    });
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/*Asset 영역*/}
                            {assetItems?.length > 0 && (
                                <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                    <Grid container flexDirection={"row"} alignItems={"center"}>
                                        <Grid container flexDirection={"row"} alignItems={"center"} flex={1}>
                                            <Grid item>
                                                <Typography variant={"body1_500"} color={theme.palette.secondary[800]}>
                                                    {t(`common::label::Selected Assets`)}
                                                </Typography>
                                            </Grid>

                                            {isAssetsCountError && (
                                                <Grid item>
                                                    <StyledErrorAlert
                                                        icon={
                                                            <IconInfoCircle
                                                                size={16}
                                                                htmlColor={theme.palette.error[700]}
                                                            />
                                                        }
                                                        color="error"
                                                        variant="outlined"
                                                    >
                                                        <Typography
                                                            variant={"body1_400"}
                                                            sx={{ color: theme.palette.secondary[800] }}
                                                        >
                                                            <Trans
                                                                i18nKey="common::msg::Size of files Option : If you choose your own assets, you can have up to {{maxAssets}}. <NewLine/> Keep the number of selected assets under {{maxAssets}}."
                                                                values={{ maxAssets: MAX_ASSETS }}
                                                                components={{
                                                                    NewLine: <Typography />,
                                                                }}
                                                            />
                                                        </Typography>
                                                    </StyledErrorAlert>
                                                </Grid>
                                            )}
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                size={"small"}
                                                variant={"text"}
                                                startIcon={<IconResetSmall size={16} />}
                                                color={"greySecondary"}
                                                onClick={() => setSelectedAssets(assetItems)}
                                            >
                                                <Typography variant={"body3"}>{t(`common::label::Reset`)}</Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <AutocompleteTableList
                                        type={"ASSET"}
                                        data={[{ value: selectedAssets }]}
                                        columns={LinkedAssetsColumn}
                                        getMenuActions={(item) => (
                                            <Tooltip title={t(`common::label::Delete`)}>
                                                <IconButton
                                                    size="medium"
                                                    circled
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onHandleDeleteAsset(item.id);
                                                    }}
                                                >
                                                    <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        noDataContent={
                                            <Grid
                                                container
                                                sx={{
                                                    height: "60px",
                                                    p: 1.5,
                                                    borderRadius: `4px`,
                                                    border: `1px solid ${theme.palette.greySecondary[100]}`,
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography>
                                                    {t(
                                                        `common::msg::There are no assets selected.`,
                                                        `There are no assets selected.`,
                                                    )}
                                                </Typography>
                                            </Grid>
                                        }
                                        divider={true}
                                        gridStyle={{ maxHeight: "336px" }}
                                    />
                                </Grid>
                            )}
                            {assetFolder?.id && (
                                <Grid container alignItems="start" flexDirection={`column`} gap={1}>
                                    <Grid container flexDirection={"row"} alignItems={"center"}>
                                        <Grid item flex={1}>
                                            <Typography variant={"body1_500"} color={theme.palette.secondary[800]}>
                                                {t(`common::label::Selected Assets`)}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                size={"small"}
                                                variant={"text"}
                                                startIcon={<IconResetSmall size={16} />}
                                                color={"greySecondary"}
                                                onClick={() => {
                                                    setEditValue({
                                                        ...editValue,
                                                        items: {
                                                            virtualFolder: {
                                                                id: assetFolder.id,
                                                                recursive: true,
                                                            },
                                                        },
                                                    });
                                                }}
                                            >
                                                <Typography variant={"body3"}>{t(`common::label::Reset`)}</Typography>
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <StyledGrid item theme={theme}>
                                        {editValue.items?.virtualFolder ? (
                                            <Grid container flexDirection={"row"}>
                                                <Grid item flex={1} sx={{ display: "flex", alignItems: "center" }}>
                                                    <Grid item mr={1}>
                                                        <ImageFolderSmallOn width={16} height={16} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography
                                                            variant={"body1_400"}
                                                            color={theme.palette.secondary[800]}
                                                        >
                                                            {assetFolder?.name}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                                <Grid item sx={{ display: "flex", alignItems: "center" }}>
                                                    <Grid item>
                                                        <Typography
                                                            variant="body1_400"
                                                            color={theme.palette.secondary[800]}
                                                        >
                                                            {editValue.items?.virtualFolder?.recursive
                                                                ? t(
                                                                      `common::label::${"Include subfolders"}`,
                                                                      "Include subfolders",
                                                                  )
                                                                : t(
                                                                      `common::label::${"Include subfolders"}`,
                                                                      "Include subfolders",
                                                                  )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Switch
                                                            color="success"
                                                            checked={editValue.items?.virtualFolder?.recursive}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                setEditValue({
                                                                    ...editValue,
                                                                    items: {
                                                                        virtualFolder: {
                                                                            id: editValue.items?.virtualFolder?.id,
                                                                            recursive: e.target.checked,
                                                                        },
                                                                    },
                                                                });
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item>
                                                    <Tooltip title={t(`common::label::Delete`)}>
                                                        <IconButton
                                                            size="medium"
                                                            circled
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const { items, ...rest } = editValue;
                                                                setEditValue(rest);
                                                            }}
                                                        >
                                                            <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Grid>
                                            </Grid>
                                        ) : (
                                            <Grid
                                                item
                                                sx={{
                                                    height: "34px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Typography>
                                                    {t(
                                                        `common::msg::There are no assets selected.`,
                                                        `There are no assets selected.`,
                                                    )}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </StyledGrid>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button color="secondary" onClick={onClose} variant="text">
                        {t(`common::label::Cancel`, `Cancel`)}
                    </Button>
                    <Button
                        color="primary"
                        onClick={onHandleCreateCollection}
                        variant="contained"
                        loading={collection.pending}
                        disabled={
                            name.validationErrors ||
                            !name.value ||
                            description.validationErrors ||
                            isAssetsCountError ||
                            collection.pending
                        }
                    >
                        {t(`common::label::Create`, `Create`)}
                    </Button>
                </DialogActions>
            </StickyHeaderDialog>
        </>
    );
};

export default DialogCreateCollection;
